import { get, isEmpty } from 'lodash'
import typescriptNaturalSort from 'typescript-natural-sort'
import { neighborhoodsEnabled } from '@utils/product'
import { IFloorMapSpace, ISpace, IUserContext, RawViewFilters, ViewFilterChild } from '@api'

export const CIRCLES = 'Hide Circles'
export const SPACE_CODES = 'Codes'
export const EMPLOYEE_NAME = 'Employee name'
export const PHONE_NUMBER = 'Phone number'
export const EMPLOYEE_TITLES = 'Employee Titles'
export const TITLES_ID = 'employeeTitles'
export const TITLES_KEY = 'title'
export const TITLES_OBJECT_TYPE = 'title'
export const OTHER_SPACES_ICONS = 'Other Spaces: Hide Icons'
export const OTHER_SPACES_NAMES = 'Other Spaces: Hide Names'
export const AREA = 'Department Areas'
export const WORKPLACE_GROUPS_EMPLOYEES = 'Employee Workplace groups'
export const WORKPLACE_GROUPS_SPACES = 'Seat Workplace groups'
export const NEIGHBORHOODS = 'Neighborhoods'
export const TEAMS = 'Team Areas'
export const NAME = 'Names instead of markers'
export const PHONE_NAME = 'Phone number'
export const CODE_NAME = 'Space code'
export const SAFE_DISTANCING_ALLOCATED = 'Allocated'
export const SAFE_DISTANCING_NOT_ALLOCATED = 'Not allocated'
export const SAFE_DISTANCING_SEATED = 'Seated'
export const SAFEDISTANCING = 'safedistancing'
export const GENERAL = 'general'
export const MAPDETAILS = 'mapdetails'

const invertedParents = [GENERAL, MAPDETAILS, SAFEDISTANCING]

export type SpacesWithIDType = {
  byId: any
  ids: string[]
  raw: ISpace[]
}
type Meta = {
  viewFilters: RawViewFilters
  context: IUserContext | undefined
  spaces: SpacesWithIDType
  product: {
    features: string[]
  }
}

export enum GeneralFilters {
  GENERAL_DEPARTMENT_AREAS = 'general_Department Areas',
  DEPARTMENT = 'aDepartment',
  GENERAL_NEIGHBORHOODS = 'general_Neighborhoods',
  NEIGHBORHOODS = 'aNeighborhood',
  GENERAL_EMPLOYEE_WORKPLACE_GROUPS = 'general_Employee Workplace groups',
  WORKPLACE_GROUP = 'aEWorkplaceGroup',
  GENERAL_SEAR_WORKPLACE_GROUPS = 'general_Seat Workplace groups',
  SEAT_WORKPLACE_GROUP = 'aWorkplaceGroup',
  GENERAL_TEAM_AREA = 'general_Team Areas',
  TEAM = 'aTeam',
}

export const STATIC_FILTERS = [
  {
    name: 'Safe distancing',
    id: 'safedistancing',
    objectType: 'safedistancing',
    filters: [
      {
        name: 'Allocated',
        tooltip: 'allocated tooltip',
        key: 'allocatedSafeDistancing',
        visible: false,
      },
      {
        name: 'Not allocated',
        tooltip: 'not allocated tooltip',
        key: 'notAllocatedSafeDistancing',
        visible: false,
      },
      {
        name: 'Seated',
        tooltip: 'seated tooltip',
        key: 'seatedSafeDistancing',
        visible: false,
      },
    ],
  },
  {
    name: 'General',
    id: 'general',
    objectType: 'general',
    filters: [
      {
        name: 'Live mode',
        tooltip: 'Toggle live mode',
        key: 'live mode',
        visible: false,
        enabled: ({ meta }: { meta: Meta }) =>
          get(meta, 'company.integrations.active', []).find(
            ({ source }: any) => source === 'vergesense'
          ),
      },
      {
        name: AREA,
        tooltip: 'highlight department clusters',
        key: 'area',
        visible: false,
      },
      {
        name: NEIGHBORHOODS,
        tooltip: 'highlight neighborhoods clusters',
        key: 'neighborhoods',
        visible: false,
        enabled: ({ meta }: { meta: Meta }) => neighborhoodsEnabled(meta.product),
      },
      {
        name: WORKPLACE_GROUPS_EMPLOYEES,
        tooltip: 'highlight seat workplace groups clusters',
        key: 'workplaceGroupsEmployees',
        visible: false,
      },
      {
        name: WORKPLACE_GROUPS_SPACES,
        tooltip: 'highlight employee workplace groups clusters',
        key: 'workplaceGroupsSpaces',
        visible: false,
      },
      {
        name: TEAMS,
        tooltip: 'highlight team clusters',
        key: 'teams',
        visible: false,
      },
    ],
  },
  {
    name: 'Map Details',
    id: 'mapdetails',
    objectType: 'mapdetails',
    filters: [
      {
        name: CIRCLES,
        tooltip: 'always hide circles',
        key: 'circles',
        visible: false,
      },
      {
        name: SPACE_CODES,
        tooltip: 'always show space codes',
        key: 'codes',
        visible: false,
      },
      {
        name: EMPLOYEE_NAME,
        tooltip: 'always show employee name',
        key: 'name',
        visible: false,
      },
      {
        name: PHONE_NUMBER,
        tooltip: 'always show employee phone number',
        key: 'phone',
        visible: false,
      },
      {
        name: OTHER_SPACES_ICONS,
        tooltip: 'hide icons for other spaces',
        key: 'icons',
        visible: false,
      },
      {
        name: OTHER_SPACES_NAMES,
        tooltip: 'show names for other spaces',
        key: 'names',
        visible: false,
      },
    ],
  },
  {
    name: 'Changes',
    id: 'changes',
    objectType: 'changes',
    filters: [
      {
        name: 'Allocation',
        key: 'allocation',
        tooltip: 'show spaces with allocation changes',
        visible: true,
        disableOnPrimary: true,
      },
      {
        name: 'Employment start/end',
        key: 'employment',
        tooltip: 'show employees with approaching start and/or end dates',
        visible: true,
      },
      {
        name: 'Seat assignment',
        key: 'seatAssignment',
        tooltip: 'show moved-into seats',
        visible: true,
        disableOnPrimary: true,
      },
      {
        name: 'No change',
        key: 'noChange',
        tooltip: 'show spaces without changes',
        visible: true,
      },
    ],
  },
  {
    name: 'Allocations',
    id: 'allocations',
    objectType: 'allocations',
    filters: [
      {
        name: 'Allocated',
        key: 'allocated',
        tooltip: 'show allocated spaces',
        visible: true,
      },
      {
        name: 'Not allocated',
        key: 'unallocated',
        tooltip: 'show not allocated spaces',
        visible: true,
      },
      {
        name: 'Seated',
        key: 'seated',
        tooltip: 'show spaces with employees',
        visible: true,
      },
    ],
  },
]

export const getByID = (rawData: Record<string, Record<string, unknown>>) => {
  return Object.keys(rawData).reduce(
    (result, filter) => ({
      ...result,
      [filter]: rawData[filter],
    }),
    {}
  )
}

export const rawToFilters = (meta: Meta) => {
  const dynamic = convertDynamicFilters(meta)
  const _static = convertStaticFilters(meta)

  return {
    ...dynamic,
    ..._static,
    filterBlackList: [...dynamic.filterBlackList, ..._static.filterBlackList],
    byId: { ...dynamic.byId, ..._static.byId },
  }
}

const convertDynamicFilters = ({ viewFilters, context }: Meta) => {
  return viewFilters.reduce(
    (
      { dynamicFilterIds, byId, filterBlackList }: any,
      { filters, objectType: filterId, ...prest }: any
    ) => {
      const _byId = {
        [filterId]: { id: filterId, visible: 0, filters: 0, ...prest },
      }

      const children = filters.map(({ displayName, ...crest }: Record<string, unknown>) => {
        const id: any = `${filterId}_${displayName}`
        const visible =
          typeof context?.filterVisibilityMap?.[id] === 'boolean'
            ? context?.filterVisibilityMap?.[id]
            : crest.visible

        _byId[id] = {
          ...crest,
          id,
          parentId: filterId,
          name: displayName,
          visible,
        }

        if (_byId[id].visible) {
          _byId[filterId].visible++
        } else {
          filterBlackList.push(_byId[id])
        }

        //filter count is used to manage state of the parent eye
        _byId[filterId].filters++
        return id
      })

      return {
        dynamicFilterIds: [
          ...dynamicFilterIds,
          {
            filterId,
            children: children.sort((a: string, b: string) => typescriptNaturalSort(a, b)),
          },
        ],
        byId: { ...byId, ..._byId },
        filterBlackList,
      }
    },
    { dynamicFilterIds: [], byId: {}, filterBlackList: [] }
  )
}

const convertStaticFilters = (meta: Meta) => {
  const { context } = meta

  const staticFilterIds: ViewFilterChild[] = []
  const byId: any = {}
  const filterBlackList: any = []

  withTitleFilter(meta)
    .map((filters: { filters: RawViewFilters }) => {
      // Remove from the list disabled filters
      filters.filters = filters.filters.filter(
        (filter: any) => !filter.enabled || filter.enabled({ meta })
      )
      return filters
    })
    .forEach(({ id: filterId, ...filter }: any) => {
      const children: string[] = []
      byId[filterId] = { ...filter, filters: 0, visible: 0 }

      filter.filters.forEach((f: any) => {
        // if (f.beta && !isBetaEnabled()) {
        if (f.beta) {
          return
        }
        const id = `${filterId}_${f.name}`

        const visible =
          typeof context?.filterVisibilityMap?.[id] === 'boolean'
            ? context?.filterVisibilityMap?.[id]
            : f.visible

        byId[id] = {
          ...f,
          id,
          parentId: filterId,
          visible,
        }

        if (invertedParents.includes(filterId) && byId[id].visible) {
          filterBlackList.push(byId[id])
        }

        if (!invertedParents.includes(filterId) && !byId[id].visible) {
          filterBlackList.push(byId[id])
        } else {
          if (filterId === 'safedistancing') {
            if (byId[id].visible) {
              byId[filterId].visible++
            }
          } else {
            byId[filterId].visible++
          }
        }

        byId[filterId].filters++

        children.push(id)
      })

      staticFilterIds.push({ filterId, children })
    })

  return {
    byId,
    staticFilterIds,
    filterBlackList,
  }
}

const withTitleFilter = (meta: Meta) => {
  const filters = getTitlesChildrenFilters(meta)
  return isEmpty(filters)
    ? STATIC_FILTERS
    : [
        ...STATIC_FILTERS,
        {
          filters,
          name: EMPLOYEE_TITLES,
          id: TITLES_ID,
          objectType: TITLES_OBJECT_TYPE,
        },
      ]
}

const getTitlesChildrenFilters = (meta: Meta) => {
  const spaces = get(meta, 'spaces.raw')

  return spaces.reduce((acc: any, space: ISpace) => {
    const employee = get(space, 'spaceAssignment.employee')

    if (!employee || !employee.title) return acc

    return [
      ...acc,
      {
        id: `${TITLES_ID}_${employee.title}`,
        name: employee.title,
        value: employee.title,
        key: TITLES_KEY,
        tooltip: `show ${employee.title}`,
        visible: true,
      },
    ]
  }, [])
}

export const filterViewFiltersForFloor = ({
  viewFilters,
  spaces,
}: {
  viewFilters: RawViewFilters
  spaces: IFloorMapSpace[]
}) => {
  if (!viewFilters) {
    return {}
  }

  const eWpgList: any = {}
  const sWpgList: any = {}
  const spaceFilterMap: any = spaces.reduce(
    (sm: any, s: any) => {
      const depId = get(s, 'spaceAssignment.department.id')
      const wpgId = get(s, 'spaceAssignment.employee.workplaceGroup.id')
      const sWpgId = get(s, 'workplaceGroup.id')
      if (depId) {
        sm.department[depId] = true
      }
      if (wpgId) {
        eWpgList[wpgId] = get(s, 'spaceAssignment.employee.workplaceGroup')
      }
      if (sWpgId) {
        sWpgList[sWpgId] = get(s, 'workplaceGroup')
      }
      return sm
    },
    { department: {}, workplaceGroup: {}, sWorkplaceGroup: {} }
  )

  viewFilters.push(
    {
      name: 'Employee Workplace Groups',
      objectType: 'employeeWorkplaceGroup',
      filters: Object.values(eWpgList).map(({ id, name }: any) => ({
        key: 'id',
        visible: true,
        displayName: name,
        value: id,
      })),
    },
    {
      name: 'Space Workplace Groups',
      objectType: 'workplaceGroup',
      filters: Object.values(sWpgList).map(({ id, name }: any) => ({
        key: 'id',
        visible: true,
        displayName: name,
        value: id,
      })),
    }
  )
  return viewFilters.reduce((newFilters: any, filter: any) => {
    if (filter.name === 'Departments') {
      newFilters.push({
        ...filter,
        filters: filter.filters.reduce((r: any, f: any) => {
          if (spaceFilterMap.department[f.value]) {
            r.push(f)
          }
          return r
        }, []),
      })
    } else if (
      filter.name === 'Employee Workplace Groups' ||
      filter.name === 'Space Workplace Groups'
    ) {
      newFilters.push({
        ...filter,
        filters: filter.filters
          .reduce((r: any, f: any) => {
            r.push(f)
            return r
          }, [])
          .concat({
            displayName: 'No workplace group',
            key: 'id',
            value: 'no_wpg',
            visible: true,
          }),
      })
    } else {
      newFilters.push(filter)
    }
    return newFilters
  }, [])
}

export const getFilterNameByID = (id: string) =>
  (GeneralFilters.GENERAL_DEPARTMENT_AREAS === id && GeneralFilters.DEPARTMENT) ||
  (GeneralFilters.GENERAL_NEIGHBORHOODS == id && GeneralFilters.NEIGHBORHOODS) ||
  (GeneralFilters.GENERAL_EMPLOYEE_WORKPLACE_GROUPS == id && GeneralFilters.WORKPLACE_GROUP) ||
  (GeneralFilters.GENERAL_TEAM_AREA == id && GeneralFilters.TEAM) ||
  (GeneralFilters.GENERAL_SEAR_WORKPLACE_GROUPS == id && GeneralFilters.SEAT_WORKPLACE_GROUP)
